
/**
 * Auth cleanup utility
 * Provides functions to clean up auth state and recover from auth-related errors
 */

import { trackError } from '../error/errorTracker';

/**
 * Perform auth state cleanup for recovery from auth-related issues
 * @returns Object with status of cleanup operations
 */
export function performAuthCleanup(): { 
  success: boolean; 
  tokensRemoved: number;
  authItemsRemoved: number;
} {
  let tokensRemoved = 0;
  let authItemsRemoved = 0;
  let success = false;
  
  try {
    // Only proceed if localStorage is available
    if (typeof window === 'undefined' || !window.localStorage) {
      return { success: false, tokensRemoved, authItemsRemoved };
    }
    
    // Get all keys in localStorage
    const allKeys = Object.keys(localStorage);
    
    // Find auth-related items
    const authKeys = allKeys.filter(key => 
      key.startsWith('supabase.auth.token') || 
      key.includes('auth') ||
      key.includes('session') ||
      key.includes('user') ||
      key.includes('token') ||
      key.includes('login')
    );
    
    // Find token-specific items
    const tokenKeys = authKeys.filter(key => 
      key.includes('token') || 
      key.includes('jwt') ||
      key.includes('refresh')
    );
    
    // Remove token-related items
    tokenKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
        tokensRemoved++;
      } catch (e) {
        console.warn(`Failed to remove token key: ${key}`, e);
      }
    });
    
    // Remove other auth-related items
    const otherAuthKeys = authKeys.filter(key => !tokenKeys.includes(key));
    otherAuthKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
        authItemsRemoved++;
      } catch (e) {
        console.warn(`Failed to remove auth key: ${key}`, e);
      }
    });
    
    // Also clear sessionStorage in case auth state is stored there
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        // Find auth-related items in sessionStorage
        const sessionKeys = Object.keys(sessionStorage);
        const sessionAuthKeys = sessionKeys.filter(key => 
          key.includes('auth') ||
          key.includes('session') ||
          key.includes('user') ||
          key.includes('token') ||
          key.includes('login')
        );
        
        // Remove them
        sessionAuthKeys.forEach(key => {
          try {
            sessionStorage.removeItem(key);
            authItemsRemoved++;
          } catch (e) {
            // Silent fail for session keys
          }
        });
      } catch (e) {
        console.warn('Failed to access sessionStorage', e);
      }
    }
    
    success = true;
    
    // Log success for analytics
    console.log(`Auth cleanup completed: removed ${tokensRemoved} tokens and ${authItemsRemoved} auth items`);
  } catch (e) {
    // Log error but don't prevent recovery
    console.error('Error during auth cleanup:', e);
    
    // Track this error for monitoring
    trackError(e, 'AuthCleanup', {
      operation: 'performAuthCleanup'
    });
    
    success = false;
  }
  
  return { success, tokensRemoved, authItemsRemoved };
}

/**
 * Detect and resolve auth limbo states
 * @returns True if a limbo state was detected and resolved
 */
export function detectAndResolveAuthLimbo(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    
    // Check for conflicting auth state indicators
    const hasAuthToken = Object.keys(localStorage).some(key => 
      key.includes('auth.token') || key.includes('auth.refresh')
    );
    
    // Check for partial auth data which can cause limbo
    const hasPartialAuth = Object.keys(localStorage).some(key => 
      (key.includes('auth') && !key.includes('token')) || key.includes('session')
    );
    
    // Look for failed logout indicators
    const hasLogoutFlag = localStorage.getItem('auth_logout_attempted') === 'true';
    
    if ((hasAuthToken && hasLogoutFlag) || (hasPartialAuth && !hasAuthToken)) {
      console.log('Auth limbo detected, cleaning up auth state');
      performAuthCleanup();
      localStorage.removeItem('auth_logout_attempted');
      
      return true;
    }
    
    return false;
  } catch (e) {
    console.warn('Error checking for auth limbo:', e);
    return false;
  }
}

/**
 * Prepare for a clean login by removing all auth-related data
 * @returns Status of cleanup operation
 */
export function prepareForCleanLogin(): boolean {
  try {
    const { success } = performAuthCleanup();
    
    // Also set a flag to indicate clean login is needed
    if (success && typeof localStorage !== 'undefined') {
      localStorage.setItem('need_clean_login', 'true');
      localStorage.setItem('clean_login_time', new Date().toISOString());
    }
    
    return success;
  } catch (e) {
    console.error('Error preparing for clean login:', e);
    return false;
  }
}

/**
 * Check if a clean login is needed
 * @returns True if clean login is needed
 */
export function isCleanLoginNeeded(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    
    return localStorage.getItem('need_clean_login') === 'true';
  } catch (e) {
    console.warn('Error checking clean login status:', e);
    return false;
  }
}

/**
 * Mark clean login as completed
 */
export function markCleanLoginComplete(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    
    localStorage.removeItem('need_clean_login');
    localStorage.setItem('last_clean_login', new Date().toISOString());
  } catch (e) {
    console.warn('Error marking clean login complete:', e);
  }
}

export default {
  performAuthCleanup,
  prepareForCleanLogin,
  isCleanLoginNeeded,
  markCleanLoginComplete,
  detectAndResolveAuthLimbo
};
